import React, { FC, FormEvent } from 'react';
import { Modal as ModalMui } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CloseIcon } from 'application/assets';

import { Loader } from 'common';

import { PrimaryButton, SecondaryButton } from '../index';

import { ModalProps } from './models';
import { Wrapper, HeaderStyled, ChildrenStyled, ButtonWrapper } from './styles';

/**
 * Functional component for a modal dialog that can be used to manage table data.
 *
 * @param opened - A boolean indicating whether the modal is currently open or closed.
 * @param onClose - A function that will be called when the user clicks the close button.
 * @param title - The text to display in the header of the modal.
 * @param onSubmit - A function that will be called when the form within the modal is submitted.
 * @param disabledSubmit - A boolean indicating whether the submit button should be disabled or not.
 * @param children - Any child elements to render within the body of the modal.
 * @param isLoading - A boolean indicating whether a loading spinner should be displayed or not.
 *
 * @returns A React component that renders the modal dialog with the given properties.
 */
const ManageTableModal: FC<ModalProps> = ({
  opened,
  onClose,
  title,
  onSubmit,
  disabledSubmit,
  children,
  isLoading,
}) => {
  const { t } = useTranslation();

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(event);
  };

  return (
    <ModalMui open={opened}>
      <>
        {isLoading && <Loader />}
        <Wrapper onSubmit={handleSubmit}>
          <HeaderStyled>
            <h1>{title}</h1>
            <button type="button" onClick={onClose}>
              <CloseIcon />
            </button>
          </HeaderStyled>
          <ChildrenStyled className={'children-wrapper'}>
            {children}
          </ChildrenStyled>
          <ButtonWrapper>
            <SecondaryButton type={'button'} onClick={onClose}>
              {t('global.cancel')}
            </SecondaryButton>
            <PrimaryButton
              type={'submit'}
              disabled={disabledSubmit}
              onClick={onSubmit}
            >
              {t('global.save')}
            </PrimaryButton>
          </ButtonWrapper>
        </Wrapper>
      </>
    </ModalMui>
  );
};

export default ManageTableModal;
